import { useEffect, useState } from "react";
import { ApiError, useAddToList, useCreateList, useLists } from "../lib/api";

type Props = {
  open: boolean;
  siteIds: number[];
  onClose: () => void;
  onDone?: (listId: number, listName: string) => void;
};

/** Pick an existing list or create one, then add the given sites (PRD §12.2 results strip, §12.1 table bulk action). */
export function AddToListDialog({ open, siteIds, onClose, onDone }: Props) {
  const lists = useLists();
  const create = useCreateList();
  const add = useAddToList();
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [listId, setListId] = useState<number | undefined>(undefined);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName("");
    const first = lists.data?.items[0];
    if (first) {
      setMode("existing");
      setListId(first.id);
    } else {
      setMode("new");
    }
  }, [open, lists.data]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const busy = create.isPending || add.isPending;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "new") {
        const created = await create.mutateAsync({
          name: name.trim() || "Untitled list",
          site_ids: siteIds,
        });
        onDone?.(created.id, created.name);
      } else if (listId !== undefined) {
        await add.mutateAsync({ listId, siteIds });
        onDone?.(listId, lists.data?.items.find((l) => l.id === listId)?.name ?? "list");
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Request failed");
    }
  }

  return (
    <div className="dialog-backdrop">
      <form
        className="dialog"
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="atl-title"
      >
        <h2 id="atl-title" className="text-[14px] font-semibold">
          Add {siteIds.length} site{siteIds.length === 1 ? "" : "s"} to a list
        </h2>
        <div className="mt-3 flex flex-col gap-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mode"
              checked={mode === "existing"}
              onChange={() => setMode("existing")}
              disabled={!lists.data || lists.data.items.length === 0}
            />
            Existing list
          </label>
          {mode === "existing" ? (
            <select
              className="input ml-5"
              value={listId ?? ""}
              onChange={(e) => setListId(Number(e.target.value))}
              aria-label="Choose list"
            >
              {(lists.data?.items ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.item_count})
                </option>
              ))}
            </select>
          ) : null}
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mode"
              checked={mode === "new"}
              onChange={() => setMode("new")}
            />
            New list
          </label>
          {mode === "new" ? (
            <input
              className="input ml-5"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="List name"
              aria-label="New list name"
              data-testid="new-list-name"
            />
          ) : null}
        </div>
        {error ? (
          <p className="mt-2 text-[12px]" style={{ color: "var(--danger)" }}>
            {error}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || siteIds.length === 0}
            data-testid="add-to-list-submit"
          >
            {busy ? "Saving…" : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
}
